import { Component, ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { withTranslation, WithTranslation } from "react-i18next";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isChunkLoadError, reloadOnceOnChunkError } from "@/lib/appVersion";

interface Props extends WithTranslation {
  children: ReactNode;
  /** Changes whenever the route path changes — used to auto-reset the boundary. */
  resetKey: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  resetKey: string;
}

/**
 * Per-route error boundary. Sits inside each layout (App/Admin/Marketing) so
 * a crashing page does NOT take down the sidebar/topbar/marketing chrome,
 * unlike GlobalErrorBoundary which is a last-resort full-screen fallback.
 *
 * Auto-resets when the user navigates to a different path so a broken page
 * does not "stick" after the user clicks another menu item.
 */
class RouteErrorBoundaryInner extends Component<Props, State> {
  state: State = { hasError: false, error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, error: null, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error("[RouteErrorBoundary]", error, info.componentStack);

    // A stale page chunk after a new release: reload once per browser tab to
    // pick up the new build, through the SAME guard the window-level handler
    // uses so one failure can never reload twice. Never classify a generic
    // "Failed to fetch" as a stale chunk: ordinary API/network failures can
    // repeat for one tenant and would create an endless reload cycle.
    if (isChunkLoadError(error?.message || "")) reloadOnceOnChunkError();
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleBack = () => {
    this.handleRetry();
    if (window.history.length > 1) window.history.back();
    else window.location.href = "/";
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { t } = this.props;
    const title = t("errors.renderTitle", { defaultValue: "تعذّر عرض هذه الصفحة" });
    const desc = t("errors.renderDesc", {
      defaultValue:
        "حدث خطأ غير متوقع أثناء تجهيز العرض. يمكنك المحاولة مرة أخرى أو العودة للخلف.",
    });
    const retry = t("common.retry", { defaultValue: "إعادة المحاولة" });
    const back = t("common.back", { defaultValue: "رجوع" });

    return (
      <div
        role="alert"
        className="rounded-lg border border-destructive/30 bg-destructive/5 p-5 max-w-2xl mx-auto"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-destructive">{title}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
            {this.state.error?.message && (
              <pre className="mt-3 max-h-40 overflow-auto rounded bg-muted p-2 text-xs text-muted-foreground whitespace-pre-wrap">
                {this.state.error.message}
              </pre>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={this.handleRetry} className="gap-2">
                <RefreshCw className="h-4 w-4" /> {retry}
              </Button>
              <Button size="sm" variant="ghost" onClick={this.handleBack} className="gap-2">
                <ArrowLeft className="h-4 w-4" /> {back}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const TranslatedRouteErrorBoundary = withTranslation()(RouteErrorBoundaryInner);

/** Convenience wrapper that derives `resetKey` from the current pathname. */
export const RouteErrorBoundary = ({ children }: { children: ReactNode }) => {
  const location = useLocation();
  return (
    <TranslatedRouteErrorBoundary resetKey={location.pathname}>
      {children}
    </TranslatedRouteErrorBoundary>
  );
};

export default RouteErrorBoundary;
